import { DevTool } from '@hookform/devtools';
import type { Ref } from 'react';
import {
  type Control,
  type FieldErrors,
  type FieldValues,
  FormProvider,
  type FormProviderProps,
} from 'react-hook-form';
import { Box } from 'ui';

type Props<TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues> = FormProviderProps<
  TFieldValues,
  TContext,
  TTransformedValues
> & {
  onSubmit?: (data: TTransformedValues) => void;
  'data-testid'?: string;
  onError?: (errors: FieldErrors<TFieldValues>) => void;
  devtool?: boolean;
  formRef?: Ref<HTMLElement>;
  density?: 'dense' | 'normal' | 'max';
};

export const Form = <TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues>({
  children,
  onSubmit,
  onError,
  devtool = true,
  'data-testid': dataTestid,
  formRef,
  density = 'normal',
  ...rest
}: Props<TFieldValues, TContext, TTransformedValues>) => {
  return (
    <FormProvider {...rest}>
      <Box
        ref={formRef}
        component="form"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: density === 'dense' ? 1 : density === 'max' ? 0 : 2,
          width: '100%',
        }}
        autoComplete="off"
        onSubmit={
          onSubmit
            ? (e) => {
                rest.clearErrors();
                rest.handleSubmit(onSubmit, onError)(e);
              }
            : undefined
        }
        noValidate
        data-testid={dataTestid}
      >
        {children}
        {devtool && !import.meta.env.PROD ? (
          <DevTool control={rest.control as unknown as Control<TFieldValues>} placement="top-right" />
        ) : null}
      </Box>
    </FormProvider>
  );
};
